import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import React, { ReactNode } from 'react';

const statVariants = cva('rounded-lg text-center flex flex-col justify-between gap-5', {
  variants: {
    variant: {
      primary: 'border border-primary-darker bg-primary-50/50 text-primary-darker',
      success: 'border border-success-darker bg-success-50/50 text-success-darker',
      accent: 'border border-accent-darker bg-accent-50/50 text-accent-darker',
    },
    size: {
      sm: 'p-3',
      md: 'p-6',
      lg: 'p-10',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

const titleVariant = cva('font-medium text-gray-700', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const descriptionVariant = cva('text-left flex-1', {
  variants: {
    size: {
      sm: 'text-base mb-2',
      md: 'text-lg mb-4',
      lg: 'text-2xl mb-6',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const valueVariant = cva('tracking-tighter font-bold text-left', {
  variants: {
    size: {
      sm: 'text-4xl',
      md: 'text-6xl',
      lg: 'text-8xl',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type StatProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> &
  VariantProps<typeof statVariants> & {
    title?: string | ReactNode;
    value: string | number | ReactNode;
    description: ReactNode;
    meta?: ReactNode;
  };

export function Stat({ title, value, description, meta, variant, size, className, ...props }: StatProps) {
  return (
    <div className={cn(statVariants({ variant, size }), className)} {...props}>
      {title && <h3 className={titleVariant({ size })}>{title}</h3>}
      <div className={valueVariant({ size })}>{value}</div>
      <div className={descriptionVariant({ size })}>{description}</div>
      {meta && <div className="text-right text-base italic">{meta}</div>}
    </div>
  );
}
export default Stat;
export { statVariants };
